<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';

export default {
    name: 'AddCannedAudio',
    components: {
        NextButton,
        Modal,
        AudioRecorder,
    },
    props: {
        onClose: {
            type: Function,
            default: () => { },
        },
    },
    setup() {
        return { v$: useVuelidate() };
    },
    data() {
        return {
            shortCode: '',
            subject: '',
            audioFile: null,
            audioFileName: '',
            scope: 'global',
            isSubmitting: false,
            show: true,
            // Recording state
            inputMode: 'upload', // 'upload' or 'record'
            isRecording: false,
            recordingDuration: '00:00',
            recordingState: '', // '', 'recording', 'stopped', 'playing', 'paused'
            hasRecordedAudio: false,
        };
    },
    validations: {
        shortCode: {
            required,
            minLength: minLength(2),
        },
        subject: {
            required,
        },
    },
    computed: {
        isFormInvalid() {
            return (
                this.v$.shortCode.$invalid ||
                this.v$.subject.$invalid ||
                !this.audioFile
            );
        },
        isUploadMode() {
            return this.inputMode === 'upload';
        },
        isRecordMode() {
            return this.inputMode === 'record';
        },
        recordButtonIcon() {
            if (!this.isRecording && !this.hasRecordedAudio) return '🎙️';
            if (this.isRecording) return '⏹️';
            return '🔁';
        },
        recordButtonLabel() {
            if (!this.isRecording && !this.hasRecordedAudio)
                return this.$t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.START');
            if (this.isRecording)
                return this.$t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.STOP');
            return this.$t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.RE_RECORD');
        },
        playPauseLabel() {
            if (this.recordingState === 'playing')
                return this.$t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.PAUSE');
            return this.$t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.PLAY');
        },
    },
    methods: {
        setInputMode(mode) {
            this.inputMode = mode;
            this.audioFile = null;
            this.audioFileName = '';
            this.isRecording = false;
            this.hasRecordedAudio = false;
            this.recordingDuration = '00:00';
            this.recordingState = '';
        },
        onFileChange(event) {
            const file = event.target.files[0];
            if (file && file.type.startsWith('audio/')) {
                this.audioFile = file;
                this.audioFileName = file.name;
            } else {
                useAlert(this.$t('CANNED_AUDIO_MGMT.ADD.FORM.AUDIO.ERROR'));
                event.target.value = '';
            }
        },
        onRecordProgressChanged(duration) {
            this.recordingDuration = duration;
        },
        onFinishRecorder(fileData) {
            this.recordingState = 'stopped';
            this.hasRecordedAudio = true;
            this.isRecording = false;
            this.audioFile = fileData.file;
            this.audioFileName = fileData.name;
        },
        onRecordPause() {
            this.recordingState = 'paused';
        },
        onRecordPlay() {
            this.recordingState = 'playing';
        },
        toggleRecording() {
            if (this.isRecording) {
                // Stop recording
                this.$refs.audioRecorder.stopRecording();
            } else if (this.hasRecordedAudio) {
                // Re-record: reset and will re-mount
                this.hasRecordedAudio = false;
                this.audioFile = null;
                this.audioFileName = '';
                this.recordingDuration = '00:00';
                this.recordingState = '';
                this.isRecording = true;
            } else {
                // Start recording (component auto-starts on mount)
                this.isRecording = true;
            }
        },
        togglePlayPause() {
            if (this.$refs.audioRecorder) {
                this.$refs.audioRecorder.playPause();
            }
        },
        resetForm() {
            this.shortCode = '';
            this.subject = '';
            this.audioFile = null;
            this.audioFileName = '';
            this.scope = 'global';
            this.inputMode = 'upload';
            this.isRecording = false;
            this.hasRecordedAudio = false;
            this.recordingDuration = '00:00';
            this.recordingState = '';
            this.v$.shortCode.$reset();
            this.v$.subject.$reset();
        },
        async addCannedAudio() {
            this.isSubmitting = true;
            const formData = new FormData();
            formData.append('short_code', this.shortCode);
            formData.append('subject', this.subject);
            formData.append('scope', this.scope);
            formData.append('audio', this.audioFile);

            try {
                await this.$store.dispatch('createCannedAudioResponse', formData);
                this.isSubmitting = false;
                useAlert(this.$t('CANNED_AUDIO_MGMT.ADD.API.SUCCESS_MESSAGE'));
                this.resetForm();
                this.onClose();
            } catch (error) {
                this.isSubmitting = false;
                const errorMessage =
                    error?.message ||
                    this.$t('CANNED_AUDIO_MGMT.ADD.API.ERROR_MESSAGE');
                useAlert(errorMessage);
            }
        },
    },
};
</script>

<template>
    <Modal v-model:show="show" :on-close="onClose">
        <div class="flex flex-col h-auto overflow-auto">
            <woot-modal-header :header-title="$t('CANNED_AUDIO_MGMT.ADD.TITLE')"
                :header-content="$t('CANNED_AUDIO_MGMT.ADD.DESC')" />
            <form class="flex flex-col w-full" @submit.prevent="addCannedAudio()">
                <div class="w-full">
                    <label :class="{ error: v$.shortCode.$error }">
                        {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
                        <input v-model="shortCode" type="text" :placeholder="$t('CANNED_AUDIO_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')
                            " @blur="v$.shortCode.$touch" />
                    </label>
                </div>

                <div class="w-full">
                    <label :class="{ error: v$.subject.$error }">
                        {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.SUBJECT.LABEL') }}
                        <input v-model="subject" type="text" :placeholder="$t('CANNED_AUDIO_MGMT.ADD.FORM.SUBJECT.PLACEHOLDER')
                            " @blur="v$.subject.$touch" />
                    </label>
                </div>

                <div class="w-full mb-3">
                    <label>
                        {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.AUDIO.LABEL') }}
                    </label>
                    <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-black2 w-fit">
                        <button type="button"
                            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer" :class="{
                                'bg-n-solid-3 text-n-slate-12 shadow-sm': isUploadMode,
                                'text-n-slate-11 hover:text-n-slate-12': !isUploadMode,
                            }" @click="setInputMode('upload')">
                            📁 {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.MODE.UPLOAD') }}
                        </button>
                        <button type="button"
                            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer" :class="{
                                'bg-n-solid-3 text-n-slate-12 shadow-sm': isRecordMode,
                                'text-n-slate-11 hover:text-n-slate-12': !isRecordMode,
                            }" @click="setInputMode('record')">
                            🎙️ {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.MODE.RECORD') }}
                        </button>
                    </div>
                </div>

                <div v-if="isUploadMode" class="w-full">
                    <div class="flex items-center gap-3 p-3 border border-dashed rounded-lg border-n-weak mb-4">
                        <input type="file" accept="audio/*"
                            class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-n-alpha-black2 file:text-n-slate-12 hover:file:bg-n-alpha-black3 cursor-pointer"
                            @change="onFileChange" />
                    </div>
                    <p v-if="audioFileName" class="text-xs text-n-slate-11 -mt-2 mb-2">
                        📁 {{ audioFileName }}
                    </p>
                </div>

                <div v-if="isRecordMode" class="w-full mb-4">
                    <div class="rounded-lg border border-n-weak bg-n-alpha-black1 p-4">
                        <div v-if="isRecording || hasRecordedAudio"
                            class="mb-3 rounded-lg overflow-hidden bg-n-solid-2">
                            <AudioRecorder ref="audioRecorder" audio-record-format="audio/mp3"
                                @recorder-progress-changed="onRecordProgressChanged" @finish-record="onFinishRecorder"
                                @pause="onRecordPause" @play="onRecordPlay" />
                        </div>

                        <div class="flex items-center justify-center mb-3">
                            <span class="text-2xl font-mono font-bold" :class="{
                                'text-n-ruby-11 animate-pulse': isRecording,
                                'text-n-slate-12': !isRecording,
                            }">
                                {{ recordingDuration }}
                            </span>
                        </div>

                        <div class="flex items-center justify-center gap-3">
                            <button type="button"
                                class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer"
                                :class="{
                                    'bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4':
                                        isRecording,
                                    'bg-n-brand/10 text-n-brand hover:bg-n-brand/20':
                                        !isRecording,
                                }" @click="toggleRecording">
                                {{ recordButtonIcon }} {{ recordButtonLabel }}
                            </button>

                            <button v-if="hasRecordedAudio && !isRecording" type="button"
                                class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-n-alpha-black2 text-n-slate-12 hover:bg-n-alpha-black3 transition-colors cursor-pointer"
                                @click="togglePlayPause">
                                {{
                                    recordingState === 'playing' ? '⏸️' : '▶️'
                                }}
                                {{ playPauseLabel }}
                            </button>
                        </div>

                        <p v-if="hasRecordedAudio" class="text-xs text-n-teal-11 text-center mt-2">
                            ✅ {{ $t('CANNED_AUDIO_MGMT.ADD.FORM.RECORD.READY') }}
                        </p>
                    </div>
                </div>

                <div class="w-full">
                    <label>
                        {{ $t('CANNED_AUDIO_MGMT.SCOPE.LABEL') }}
                    </label>
                    <div class="flex gap-4 mb-4">
                        <label class="flex items-center gap-2 cursor-pointer text-sm">
                            <input v-model="scope" type="radio" value="global" class="w-4 h-4" />
                            {{ $t('CANNED_AUDIO_MGMT.SCOPE.GLOBAL') }}
                        </label>
                        <label class="flex items-center gap-2 cursor-pointer text-sm">
                            <input v-model="scope" type="radio" value="personal" class="w-4 h-4" />
                            {{ $t('CANNED_AUDIO_MGMT.SCOPE.PERSONAL') }}
                        </label>
                    </div>
                </div>

                <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
                    <NextButton faded slate type="reset" :label="$t('CANNED_AUDIO_MGMT.ADD.CANCEL_BUTTON_TEXT')"
                        @click.prevent="onClose" />
                    <NextButton type="submit" :label="$t('CANNED_AUDIO_MGMT.ADD.FORM.SUBMIT')"
                        :disabled="isFormInvalid || isSubmitting" :is-loading="isSubmitting" />
                </div>
            </form>
        </div>
    </Modal>
</template>
